import React, { useEffect, useMemo, useState } from 'react';
import PrimaryNavigation from '../../components/ui/PrimaryNavigation';
import BreadcrumbNavigation from '../../components/ui/BreadcrumbNavigation';
import Icon from '../../components/AppIcon';
import Seo from '../../components/Seo';
import { supabase } from '../../lib/supabaseClient';

const Rankings = () => {
  const [isLoading, setIsLoading] = useState(true);
  const [rankings, setRankings] = useState([]);
  const now = useMemo(() => new Date(), []);
  const [selectedYear, setSelectedYear] = useState(now.getUTCFullYear());
  const [selectedMonth, setSelectedMonth] = useState(now.getUTCMonth() + 1);

  const monthOptions = useMemo(() => (
    Array.from({ length: 12 }, (_, idx) => idx + 1)
  ), []);

  const yearOptions = useMemo(() => {
    const base = now.getUTCFullYear();
    return [base, base - 1, base - 2];
  }, [now]);

  useEffect(() => {
    const loadRankings = async () => {
      setIsLoading(true);
      const { data } = await supabase.rpc('ch_get_monthly_proof_rankings', {
        p_year: selectedYear,
        p_month: selectedMonth,
        p_limit: 20,
      });
      setRankings(data || []);
      setIsLoading(false);
    };

    loadRankings();
  }, [selectedYear, selectedMonth]);

  return (
    <div className="min-h-screen bg-background">
      <Seo title="월간 랭킹" canonicalPath="/rankings" />
      <PrimaryNavigation />
      <BreadcrumbNavigation />
      <main className="pt-28 lg:pt-28 pb-20 md:pb-24 lg:pb-16 px-4 sm:px-5 md:px-6 lg:px-8">
        <div className="max-w-screen-xl mx-auto space-y-6 md:space-y-8">
          <header className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div>
              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground">
                월간 랭킹
              </h1>
              <p className="text-sm md:text-base text-muted-foreground mt-2">
                월별 인증 수 기준으로 상위 유저를 확인하세요.
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(Number(e.target.value))}
                className="px-3 py-2 rounded-md bg-muted/40 border border-border text-sm text-foreground"
                aria-label="랭킹 연도 선택"
              >
                {yearOptions.map((year) => (
                  <option key={year} value={year}>{year}년</option>
                ))}
              </select>
              <select
                value={selectedMonth}
                onChange={(e) => setSelectedMonth(Number(e.target.value))}
                className="px-3 py-2 rounded-md bg-muted/40 border border-border text-sm text-foreground"
                aria-label="랭킹 월 선택"
              >
                {monthOptions.map((month) => (
                  <option key={month} value={month}>{month}월</option>
                ))}
              </select>
            </div>
          </header>

          <div className="bg-card rounded-lg shadow-gaming-md p-4 md:p-6">
            {isLoading ? (
              <p className="text-sm text-muted-foreground caption">로딩 중...</p>
            ) : rankings.length === 0 ? (
              <p className="text-sm text-muted-foreground caption">랭킹 데이터가 없습니다.</p>
            ) : (
              <div className="space-y-3">
                {rankings.map((entry, index) => (
                  <div key={entry.user_id} className="flex items-center justify-between p-3 rounded-md border border-border bg-muted/30">
                    <div className="flex items-center gap-3">
                      <div className={`w-9 h-9 rounded-full flex items-center justify-center ${index < 3 ? 'bg-primary/20 text-primary' : 'bg-muted text-muted-foreground'}`}>
                        {index < 3 ? <Icon name="Crown" size={16} /> : <span className="text-sm font-semibold">{index + 1}</span>}
                      </div>
                      <div>
                        <p className="text-sm md:text-base font-semibold text-foreground">{entry.username}</p>
                        <p className="text-xs text-muted-foreground caption">{entry.proof_count}회 인증</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm md:text-base font-bold text-primary data-text">#{index + 1}</span>
                      <div className="w-9 h-9 rounded-full bg-card border border-border overflow-hidden flex items-center justify-center">
                        {entry.avatar_url ? (
                          <img
                            src={entry.avatar_url}
                            alt={entry.username || 'avatar'}
                            className="w-full h-full object-cover"
                            loading="lazy"
                            decoding="async"
                          />
                        ) : (
                          <Icon name="User" size={16} className="text-muted-foreground" />
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default Rankings;
